/**
 * Promotions Hook using TanStack Query
 * Replaces manual refresh with automatic cache invalidation
 */

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { Promotion } from '../../core/resources/checkout';
import { PromotionsResource } from '../../core/resources/promotions';

import { getGlobalApiClient } from './useApiQuery';

export interface UsePromotionsQueryOptions {
    checkoutSessionId?: string;
    enabled?: boolean;
}

export interface UsePromotionsQueryResult {
    // Query data
    appliedPromotions: Promotion[];
    isLoading: boolean;
    error: Error | null;

    // Loading states for mutations
    isApplying: boolean;
    isRemoving: boolean;

    // Actions
    applyPromotionCode: (code: string) => Promise<{ success: boolean; error?: string; promotion?: Promotion }>;
    removePromotionCode: (promotionId: string) => Promise<{ success: boolean; error?: string }>;
    refresh: () => Promise<void>;
    clearError: () => void;
}

export function usePromotionsQuery(options: UsePromotionsQueryOptions = {}): UsePromotionsQueryResult {
    const { checkoutSessionId, enabled = true } = options;
    const queryClient = useQueryClient();
    const client = getGlobalApiClient();

    // Create resource client
    const promotionsResource = useMemo(() => {
        try {
            return new PromotionsResource(client);
        } catch (error) {
            throw new Error('Failed to initialize promotions resource: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
    }, [client]);

    // Main promotions query
    const {
        data: appliedPromotions = [],
        isLoading,
        error,
        refetch,
    } = useQuery<Promotion[]>({
        queryKey: ['promotions', checkoutSessionId],
        queryFn: () => promotionsResource.getAppliedPromotions(checkoutSessionId!),
        enabled: enabled && !!checkoutSessionId,
        staleTime: 30_000, // 30 seconds
        refetchOnWindowFocus: false,
    });

    // Apply promotion mutation
    const applyMutation = useMutation<
        { success: boolean; error?: string; promotion?: Promotion },
        Error,
        { code: string }
    >({
        mutationFn: ({ code }) => {
            if (!checkoutSessionId) {
                throw new Error('No checkout session available');
            }

            return promotionsResource.applyPromotionCode(checkoutSessionId, code);
        },
        onSuccess: () => {
            // Invalidate related queries
            if (checkoutSessionId) {
                void queryClient.invalidateQueries({ queryKey: ['promotions', checkoutSessionId] });
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutSessionId] });
            }
        },
    });

    // Remove promotion mutation
    const removeMutation = useMutation<
        { success: boolean; error?: string },
        Error,
        { promotionId: string }
    >({
        mutationFn: ({ promotionId }) => {
            if (!checkoutSessionId) {
                throw new Error('No checkout session available');
            }

            return promotionsResource.removePromotionCode(checkoutSessionId, promotionId);
        },
        onSuccess: () => {
            // Invalidate related queries
            if (checkoutSessionId) {
                void queryClient.invalidateQueries({ queryKey: ['promotions', checkoutSessionId] });
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutSessionId] });
            }
        },
    });

    return {
        // Query data
        appliedPromotions,
        isLoading,
        error,

        // Loading states for mutations
        isApplying: applyMutation.isPending,
        isRemoving: removeMutation.isPending,

        // Actions
        applyPromotionCode: async (code: string) => {
            try {
                return await applyMutation.mutateAsync({ code });
            } catch (err) {
                const error = err instanceof Error ? err : new Error('Failed to apply promotion code');
                return { success: false, error: error.message };
            }
        },
        removePromotionCode: async (promotionId: string) => {
            try {
                return await removeMutation.mutateAsync({ promotionId });
            } catch (err) {
                const error = err instanceof Error ? err : new Error('Failed to remove promotion code');
                return { success: false, error: error.message };
            }
        },
        refresh: async () => {
            await refetch();
        },
        clearError: () => {
            // Clear query error by refetching
            void refetch();
        },
    };
}
